import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ModalService } from '../../services/modal.service';
import { SpacesService } from '../../services/spaces.service';
import { MatomoService } from '../../services/matomo.service';
import { AuthService } from '../../auth/auth.service';
import { Space } from '../../models/space.model';
import { SpaceConfigOptionsService } from '../../services/space-config-options.service';

@Component({
    selector: 'app-create-space-modal',
    imports: [FormsModule, TranslateModule],
    templateUrl: './create-space-modal.component.html',
    styleUrl: './create-space-modal.component.scss'
})
export class CreateSpaceModalComponent implements OnInit {
  isVisible = false;
  spaceName = '';
  selectedCountry = 'Germany';
  errorMessage = '';
  private readonly destroyRef = inject(DestroyRef);
  private spaceData?: Partial<Space>;

  get countries() { return this.spaceConfigOptions.countryNames; }

  constructor(
    private readonly modalService: ModalService,
    private readonly spacesService: SpacesService,
    private readonly matomoService: MatomoService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly spaceConfigOptions: SpaceConfigOptionsService
  ) {}

  ngOnInit(): void {
    this.modalService.modal$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(config => {
      this.isVisible = config.type === 'create-space';
      if (this.isVisible) {
        this.spaceName = '';
        this.spaceData = config.data;
        // Use the country from space data if available, otherwise default to Germany
        this.selectedCountry = this.spaceData?.country || 'Germany';
        this.errorMessage = '';
      }
    });
  }

  get spaceConfig() {
    const info = this.spaceConfigOptions.getCountryInfo(this.selectedCountry);
    return { language: info.code, currency: info.currency };
  }

  closeDialog(): void {
    this.modalService.closeModal();
  }

  createSpace(): void {
    const name = this.spaceName.trim();
    if (!name) {
      this.errorMessage = 'Please enter a space name';
      return;
    }

    if (this.spacesService.exists(name)) {
      this.errorMessage = 'A space with this name already exists';
      return;
    }

    const space: Space = {
      id: '',
      name,
      country: this.selectedCountry,
      languageCode: this.spaceData?.languageCode ?? 'en',
      currencyCode: this.spaceData?.currencyCode ?? this.spaceConfig.currency,
      notes: [],
      relevantIndustries: this.spaceData?.relevantIndustries ?? [],
      disciplines: this.spaceData?.disciplines ?? [],
      relevantMaterials: this.spaceData?.relevantMaterials ?? [],
      selectedSolutions: this.spaceData?.selectedSolutions ?? [],
      selectedEditions: this.spaceData?.selectedEditions ?? [],
      dateCreated: new Date(),
      ownerName: this.authService.getCurrentDisplayName(),
      status: 'Active',
      teamSize: this.spaceData?.teamSize ?? 0,
      averageHourlyRate: this.spaceData?.averageHourlyRate ?? 0,
      projectsPerYear: this.spaceData?.projectsPerYear ?? 0,
      averageLicenceCost: this.spaceData?.averageLicenceCost ?? 0,
      maturityLevel: this.spaceData?.maturityLevel ?? 20,
      productivityGainPercent: this.spaceData?.productivityGainPercent ?? 0,
      reworkReductionPercent: this.spaceData?.reworkReductionPercent ?? 0,
      coordinationSavingsPercent: this.spaceData?.coordinationSavingsPercent ?? 0,
      bookmarkedProductDemos: this.spaceData?.bookmarkedProductDemos ?? [],
      bookmarkedWorkflowDemos: this.spaceData?.bookmarkedWorkflowDemos ?? [],
      bookmarkedUseCases: this.spaceData?.bookmarkedUseCases ?? []
    };

    const isCreated = this.spacesService.add(space);
    if (isCreated) {
      this.matomoService.trackActionPageView(
        this.matomoService.CATEGORY.space,
        this.matomoService.ACTION.createSuccess,
        'create_space_modal'
      );
      this.closeDialog();
      this.router.navigate(['/spaces']);
      return;
    }

    this.errorMessage = 'Failed to create space';
  }
}
